import { atom } from 'recoil';

import type { IStep } from '@chainlit/react-client';

/**
 * Default number of user turns kept on screen. A "turn" is one top-level
 * user_message plus every following top-level entry up to the next user_message.
 * Older turns are hidden until the operator scrolls to the top, which reveals
 * one more at a time.
 */
export const USER_TURNS_TO_SHOW = 2;

/**
 * How many trailing user turns the chat currently renders. Scrolling to the top
 * increments it; sending a new message resets it to USER_TURNS_TO_SHOW.
 */
export const visibleTurnsState = atom<number>({
  key: 'visibleTurns',
  default: USER_TURNS_TO_SHOW
});

/**
 * Slice the full message list down to the last `turns` user turns.
 *
 * Composer user messages are parent-less roots, so top-level user_message
 * entries mark turn boundaries. Returns the slice plus whether older turns
 * remain hidden.
 */
export function sliceToVisibleTurns(
  messages: IStep[],
  turns: number
): { start: number; hasMore: boolean; visible: IStep[] } {
  const userIndices: number[] = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].type === 'user_message') userIndices.push(i);
  }
  const start =
    userIndices.length <= turns ? 0 : userIndices[userIndices.length - turns];
  return { start, hasMore: start > 0, visible: messages.slice(start) };
}

/** Count of top-level user_message entries — used to detect a new user turn. */
export function countUserTurns(messages: IStep[]): number {
  let n = 0;
  for (const m of messages) if (m.type === 'user_message') n++;
  return n;
}
